import {
  cardGroup,
  cardPresentation,
} from "../../../../packages/shared/src/card-presentation";
import { RequestFailure } from "../components/RequestFailure";
import { t as msg, formatNumber } from "../lib/i18n";
import { useState, useId, useRef } from "react";
import { ArrowRight, Plus, Inbox, Check, Search, RotateCw } from "lucide-react";
import type { Card, User } from "../../../../packages/shared/src/types";
import { api } from "../lib/api";
import { useCardCollection } from "../lib/useCardCollection";
import { CardPreview } from "../components/CardPreview";
import { ConfirmDialog } from "../components/ConfirmDialog";
export function MyCards({
  user,
  signIn,
}: {
  user: User | null;
  signIn: () => void;
}) {
  type Tab = "received" | "sent";
  const [tab, setTab] = useState<Tab>(() =>
    new URLSearchParams(location.search).get("tab") === "sent"
      ? "sent"
      : "received",
  );
  const [query, setQuery] = useState("");
  const received = useCardCollection("received", true, query),
    sent = useCardCollection("sent", !!user, query);
  const collection = tab === "received" ? received : sent;
  const [confirm, setConfirm] = useState<Card | null>(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [notice, setNotice] = useState("");
  const writing = useRef(false),
    id = useId();
  const category = (card: Card) =>
    cardPresentation(card).primary
      ? msg("Needs your attention")
      : cardGroup(card) === "history"
        ? msg("History")
        : msg("Your promises");
  function select(value: Tab) {
    setTab(value);
    history.replaceState(null, "", `/cards?tab=${value}`);
  }
  async function complete() {
    if (!confirm || writing.current) return;
    writing.current = true;
    setBusy(true);
    setError("");
    try {
      const result = await api<{ card: Card }>(
        `/api/redemptions/${confirm.pendingRedemption!.id}/done`,
        "POST",
        {},
      );
      sent.update(result.card);
      setConfirm(null);
      setNotice(msg("Completed"));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      writing.current = false;
      setBusy(false);
    }
  }
  return (
    <section className="wrap section cards-workspace">
      <div className="section-heading">
        <div>
          <h1 className="page-title">{msg("My cards.")}</h1>
        </div>
        <a className="button" href="/create">
          <Plus size={17} />
          {msg("Make a card")}
        </a>
      </div>
      <div className="collection-toolbar">
        <div
          className="tabs"
          role="tablist"
          aria-label={msg("Your cards")}
          onKeyDown={(e) => {
            const next =
              e.key === "Home"
                ? "received"
                : e.key === "End"
                  ? "sent"
                  : ["ArrowLeft", "ArrowRight"].includes(e.key)
                    ? tab === "sent"
                      ? "received"
                      : "sent"
                    : null;
            if (next) {
              e.preventDefault();
              select(next);
              document.getElementById(`${id}-${next}`)?.focus();
            }
          }}
        >
          {(["received", "sent"] as const).map((value) => (
            <button
              key={value}
              id={`${id}-${value}`}
              role="tab"
              aria-selected={tab === value}
              aria-controls={`${id}-panel`}
              tabIndex={tab === value ? 0 : -1}
              className={tab === value ? "active" : ""}
              onClick={() => select(value)}
            >
              {msg(value === "received" ? "Received" : "Sent")}
              <span className="tab-count">
                {formatNumber(
                  (value === "received" ? received : sent).total || 0,
                )}
              </span>
            </button>
          ))}
        </div>
        <label className="collection-search">
          <Search size={18} aria-hidden="true" />
          <span className="sr-only">{msg("Search cards")}</span>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={msg("Search cards")}
          />
        </label>
        <button
          className="icon-button"
          aria-label={msg("Refresh")}
          disabled={collection.loading}
          onClick={() => void collection.refresh()}
        >
          <RotateCw size={18} />
        </button>
      </div>
      <div
        role="tabpanel"
        id={`${id}-panel`}
        aria-labelledby={`${id}-${tab}`}
        tabIndex={0}
      >
        {tab === "received" && !user && (
          <p className="guest-keep notice">
            {msg("Cards received as a guest stay in this browser.")}{" "}
            <button className="text-button" onClick={signIn}>
              {msg("Sign in to keep them across devices")}
            </button>
          </p>
        )}
        {notice && (
          <p className="notice" role="status">
            {notice}
          </p>
        )}
        {collection.error && (
          <RequestFailure
            error={collection.error}
            retry={() => void collection.refresh()}
            signIn={signIn}
          />
        )}
        {tab === "sent" && !user ? (
          <div className="empty">
            <Inbox />
            <h2>{msg("Keep your promises close.")}</h2>
            <button className="button" onClick={signIn}>
              {msg("Sign in")}
            </button>
          </div>
        ) : collection.loading && !collection.cards.length ? (
          <p className="loading-state" role="status">
            {msg("Gathering your cards…")}
          </p>
        ) : !collection.error && !collection.cards.length ? (
          <div className="empty">
            <Inbox size={34} strokeWidth={1.3} />
            <h2>
              {query
                ? msg("No matching cards")
                : tab === "received"
                  ? msg("A little room for good things.")
                  : msg("Your first promise starts here.")}
            </h2>
            {query ? (
              <button className="text-button" onClick={() => setQuery("")}>
                {msg("Clear search")}
              </button>
            ) : (
              <a className="text-link" href="/create">
                {msg("Make a card")}
                <ArrowRight size={16} />
              </a>
            )}
          </div>
        ) : null}
        <div className="collection-grid">
          {collection.cards.map((card, index) => (
            <div key={card.id} style={{ display: "contents" }}>
              {(!index ||
                category(collection.cards[index - 1]) !== category(card)) && (
                <h2 className="collection-group-title">{category(card)}</h2>
              )}
              <article className="collection-item">
                <a className="card-open-link" href={`/cards/${card.id}`}>
                  <CardPreview card={card} />
                </a>
                <div className="item-body">
                  {cardPresentation(card).primary === "done" ? (
                    <button
                      className="button full"
                      disabled={busy}
                      onClick={() => {
                        setError("");
                        setConfirm(card);
                      }}
                    >
                      <Check size={16} />
                      {msg("Mark as done")}
                    </button>
                  ) : (
                    <a
                      className="text-link card-detail-link"
                      href={`/cards/${card.id}`}
                    >
                      {msg("Open card")}
                      <ArrowRight size={15} />
                    </a>
                  )}
                </div>
              </article>
            </div>
          ))}
        </div>
        {collection.cursor && (
          <button
            className="button secondary load-more"
            disabled={collection.more || collection.loading}
            onClick={() => void collection.moreCards()}
          >
            {collection.more ? msg("Loading…") : msg("Load more cards")}
          </button>
        )}
      </div>
      {confirm && (
        <ConfirmDialog
          title={msg("Made good on it?")}
          busy={busy}
          destructive={false}
          confirmLabel={msg("Yes, it’s done")}
          cancelLabel={msg("Cancel")}
          onConfirm={() => void complete()}
          onCancel={() => setConfirm(null)}
        >
          {msg(
            "This uses one remaining use and records completion on the card. Confirm only after you’ve kept the promise.",
          )}
          {error && (
            <span className="error" role="alert">
              {error}
            </span>
          )}
        </ConfirmDialog>
      )}
    </section>
  );
}
